import { CopyOutlined } from '@ant-design/icons'
import type { TablePaginationConfig, TableProps } from 'antd'
import { Avatar, Button, Card, message, Select, Table, Tooltip } from 'antd'
import copy from 'copy-to-clipboard'
import React, { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { logError, logInfo } from 'utils/logger'

import './style.scss'

interface UserData {
  key: string
  firstName: string
  lastName: string
  phone: string
  email: string
  legalName: string
  country: string
  walletAddress: string
}

export default function User() {
  const additionalItems = [BREADCRUMBS.USER]
  const [data, setData] = useState<UserData[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [current, setCurrent] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [email, setEmail] = useState<string | undefined>(undefined)
  const [phone, setPhone] = useState<string | undefined>(undefined)
  const [kycstatus, setKycStatus] = useState<number | undefined>(undefined)
  const [walletAddress, setWalletAddress] = useState<string | undefined>(undefined)

  // Fetch data with pagination
  const getData = useCallback(
    async (pageIndex = 1, pageSize = 10) => {
      setLoading(true)
      try {
        const res = await AppAdmin.getListUser(
          pageSize,
          pageIndex - 1,
          email,
          phone,
          kycstatus,
          walletAddress
        )
        if (res?.data.success) {
          const users = res?.data.data.map((item: any, index: number) => ({
            key: `${pageIndex}-${index}`,
            logoUrl: item?.logoUrl,
            phone: item?.phone,
            email: item?.email,
            walletAddress: item?.walletAddress,
            kycStatus: item?.kycStatus,
            kycSession: item?.kycSession,
          }))
          logInfo(users)
          setData(users)
          setTotal(res?.data.totalCount)
        }
      } catch (error) {
        setData([])
        setTotal(0)
        logError('Error fetching data: ', error)
      } finally {
        setLoading(false)
      }
    },
    [email, kycstatus, phone, walletAddress]
  )

  useEffect(() => {
    getData(current, pageSize)
  }, [current, pageSize, email, phone, kycstatus, walletAddress, getData])

  useEffect(() => {
    setCurrent(1)
    setPageSize(10)
  }, [email, phone, kycstatus, walletAddress])

  // Define table columns
  const columns: TableProps<UserData>['columns'] = [
    {
      title: 'Logo',
      dataIndex: 'logoUrl',
      key: 'logoUrl',
      width: 80,
      render: (logo) => <Avatar src={logo} size={50} />,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Wallet Address',
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://basescan.org/address/${text}`}
          >{`${text.slice(0, 8)}...${text.slice(-8)}`}</a>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Kyc Status',
      dataIndex: 'kycStatus',
      key: 'kycStatus',
      // render: (text) => {
      //   if (text === -1) return (<span>ALL</span>)
      //   // if (text === 0) return (<span>Submission required</span>)
      //   // if (text === 1) return (<span>Pending</span>)
      //   // if (text === 2) return (<span>Verified</span>)
      //   // if (text === 3) return (<span>Cancelled</span>)
      //   else return (<span>{text}</span>)
      // }
    },
    {
      title: 'Kyc Session',
      dataIndex: 'kycSession',
      key: 'kycSession',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {text.length ? (
            <>
              <a
                target="_blank"
                rel="noreferrer"
                href={`https://basescan.org/address/${text}`}
              >{`${text.slice(0, 8)}...${text.slice(-8)}`}</a>
              <Tooltip title="Copy">
                <Button
                  type="link"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    copy(text)
                    message.success('Copied Success!')
                  }}
                />
              </Tooltip>
            </>
          ) : (
            <></>
          )}
        </div>
      ),
    },
  ]

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const { current: newPage, pageSize: newPageSize } = pagination
    setCurrent(newPage || 1)
    setPageSize(newPageSize || 10)
  }

  return (
    <div className="main-section user">
      <Breadcrumb additionalItems={additionalItems} />
      <div className="main-title" style={{ marginBottom: 20 }}>
        User
      </div>
      <div className="user-search">
        <input
          type="text"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          type="phone"
          placeholder="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <Select
          placeholder="is Kyc?"
          allowClear
          options={[
            { value: -1, label: 'All' },
            { value: 0, label: 'Submission required' },
            { value: 1, label: 'Pending' },
            { value: 2, label: 'Verified' },
            { value: 3, label: 'Cancelled' },
          ]}
          value={kycstatus}
          onChange={(value) => setKycStatus(value)}
          style={{ width: 170, textAlign: 'start', height: 38 }}
        />
        <input
          type="text"
          placeholder="Wallet Address"
          value={walletAddress}
          onChange={(e) => setWalletAddress(e.target.value)}
        />
        <Button
          type="primary"
          // onClick={() => getData(current, pageSize)}
          onClick={() => {
            setCurrent(1)
            setPageSize(10)
            getData(1, 10)
          }}
        >
          Search
        </Button>
      </div>
      <Card style={{ margin: '0 auto' }}>
        <Table<UserData>
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={total < 11 ? false : { total, current, pageSize }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          bordered
        />
      </Card>
    </div>
  )
}
